import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { chatAPI, teamAPI, casesAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { useChatSocket } from '../hooks/useSocket';
import { format, isToday, isYesterday, parseISO } from 'date-fns';
import { cn } from '../lib/utils';

// UI Components
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';

// Icons
import {
  MessageSquare,
  Users,
  Briefcase,
  Search,
  Plus,
  Send,
  MoreVertical,
  UserPlus,
  Hash,
  AtSign,
  Paperclip,
  X,
  ChevronRight,
  Circle,
  CheckCheck,
  Clock,
  ArrowLeft,
  FileText,
  Calendar,
  DollarSign,
  User,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Chat type icons
const chatTypeIcons = {
  dm: User,
  group: Users,
  case: Briefcase,
};

// Format message timestamp
const formatMessageTime = (dateStr) => {
  const date = parseISO(dateStr);
  if (isToday(date)) {
    return format(date, 'h:mm a');
  }
  if (isYesterday(date)) {
    return `Yesterday ${format(date, 'h:mm a')}`;
  }
  return format(date, 'MMM d, h:mm a');
};

// Format chat list timestamp
const formatChatTime = (dateStr) => {
  if (!dateStr) return '';
  const date = parseISO(dateStr);
  if (isToday(date)) {
    return format(date, 'h:mm a');
  }
  if (isYesterday(date)) {
    return 'Yesterday';
  }
  return format(date, 'MMM d');
};

// Get initials from name
const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

// Chat List Item Component
const ChatListItem = ({ chat, isActive, unreadCount, onClick }) => {
  const TypeIcon = chatTypeIcons[chat.type] || MessageSquare;
  
  return (
    <button
      onClick={onClick}
      data-testid={`chat-item-${chat.chat_id}`}
      className={cn(
        'w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left',
        isActive 
          ? 'bg-blue-50 dark:bg-blue-900/20 border-l-2 border-blue-500' 
          : 'hover:bg-gray-50 dark:hover:bg-gray-800'
      )}
    >
      <div className="relative">
        {chat.type === 'dm' && chat.members?.[0] ? (
          <Avatar className="h-10 w-10">
            <AvatarImage src={chat.members[0].picture} />
            <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white text-sm">
              {getInitials(chat.members[0].name)}
            </AvatarFallback>
          </Avatar>
        ) : (
          <div className={cn(
            'h-10 w-10 rounded-full flex items-center justify-center',
            chat.type === 'case' ? 'bg-amber-100 text-amber-600' : 'bg-blue-100 text-blue-600'
          )}>
            <TypeIcon className="h-5 w-5" />
          </div>
        )}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 h-5 w-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <span className={cn(
            'font-medium truncate',
            unreadCount > 0 ? 'text-gray-900' : 'text-gray-700'
          )}>
            {chat.type === 'dm' && chat.members?.[0] 
              ? chat.members[0].name 
              : chat.name}
          </span>
          <span className="text-xs text-gray-400 flex-shrink-0 ml-2">
            {formatChatTime(chat.last_message_at)}
          </span>
        </div>
        {chat.last_message_preview && (
          <p className={cn(
            'text-sm truncate mt-0.5',
            unreadCount > 0 ? 'text-gray-600 font-medium' : 'text-gray-400'
          )}>
            {chat.last_message_preview}
          </p>
        )}
      </div>
    </button>
  );
};

// Allowed reactions for the emoji picker
const ALLOWED_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🎉", "🔥", "✅"];

// Message Component
const MessageBubble = ({ message, isOwn, showSender, chatId, onReactionToggle }) => {
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  
  const handleReaction = async (emoji) => {
    setShowReactionPicker(false);
    if (onReactionToggle) {
      await onReactionToggle(message.message_id, emoji);
    }
  };
  
  return (
    <div className={cn('flex gap-2 mb-3 group', isOwn ? 'flex-row-reverse' : '')}>
      {!isOwn && showSender && (
        <Avatar className="h-8 w-8 flex-shrink-0">
          <AvatarImage src={message.sender?.picture} />
          <AvatarFallback className="bg-gradient-to-br from-gray-400 to-gray-600 text-white text-xs">
            {getInitials(message.sender?.name)}
          </AvatarFallback>
        </Avatar>
      )}
      {!isOwn && !showSender && <div className="w-8" />}
      
      <div className={cn('max-w-[70%]', isOwn ? 'items-end' : 'items-start')}>
        {showSender && !isOwn && (
          <span className="text-xs text-gray-500 ml-1 mb-1 block">
            {message.sender?.name}
          </span>
        )}
        <div className="relative">
          <div className={cn(
            'px-4 py-2 rounded-2xl',
            isOwn 
              ? 'bg-blue-500 text-white rounded-br-md' 
              : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-bl-md'
          )}>
            {message.content && (
              <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
            )}
            {message.attachments?.length > 0 && (
              <div className={cn('space-y-1', message.content && 'mt-2')}>
                {message.attachments.map((att, i) => (
                  <a 
                    key={i}
                    href={att.url?.startsWith('/') ? `${BACKEND_URL}${att.url}` : att.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      'flex items-center gap-2 text-xs p-2 rounded-lg transition-colors',
                      isOwn 
                        ? 'bg-blue-400/50 hover:bg-blue-400/70 text-white' 
                        : 'bg-white/50 hover:bg-white/80 border text-gray-700'
                    )}
                  >
                    <FileText className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate">{att.filename}</span>
                  </a>
                ))}
              </div>
            )}
          </div>
          
          {/* Reaction button - shows on hover */}
          <button
            onClick={() => setShowReactionPicker(!showReactionPicker)}
            className={cn(
              'absolute -bottom-2 opacity-0 group-hover:opacity-100 transition-opacity',
              'bg-white dark:bg-gray-800 border rounded-full p-1 shadow-sm hover:bg-gray-50',
              isOwn ? 'left-0' : 'right-0'
            )}
          >
            <span className="text-xs">😊</span>
          </button>
          
          {/* Reaction picker */}
          {showReactionPicker && (
            <div className={cn(
              'absolute -bottom-10 bg-white dark:bg-gray-800 border rounded-full shadow-lg p-1 flex gap-1 z-10',
              isOwn ? 'left-0' : 'right-0'
            )}>
              {ALLOWED_REACTIONS.map(emoji => (
                <button
                  key={emoji}
                  onClick={() => handleReaction(emoji)}
                  className="hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full p-1 transition-colors"
                >
                  <span className="text-sm">{emoji}</span>
                </button>
              ))}
            </div>
          )}
          
          {/* Display existing reactions */}
          {message.reactions?.length > 0 && (
            <div className={cn(
              'flex gap-1 mt-1 flex-wrap',
              isOwn ? 'justify-end' : 'justify-start'
            )}>
              {message.reactions.map((reaction, idx) => (
                <button
                  key={idx}
                  onClick={() => handleReaction(reaction.emoji)}
                  className="bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 rounded-full px-2 py-0.5 text-xs flex items-center gap-1"
                >
                  <span>{reaction.emoji}</span>
                  <span className="text-gray-500">{reaction.count}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <span className={cn(
          'text-[10px] text-gray-400 mt-1 px-1 block',
          isOwn ? 'text-right' : 'text-left'
        )}>
          {formatMessageTime(message.created_at)}
        </span>
      </div>
    </div>
  );
};

// Case Info Panel Component
const CaseInfoPanel = ({ caseDetails, onClose }) => {
  if (!caseDetails) return null;
  
  return (
    <div className="w-80 border-l bg-white dark:bg-gray-900 flex flex-col">
      <div className="p-4 border-b flex items-center justify-between">
        <h3 className="font-semibold">Case Details</h3>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-medium text-gray-500">Case Title</h4>
            <p className="font-medium">{caseDetails.title}</p>
          </div>
          
          <div>
            <h4 className="text-sm font-medium text-gray-500">File Number</h4>
            <p>{caseDetails.case_number || caseDetails.case_id}</p>
          </div>
          
          <div>
            <h4 className="text-sm font-medium text-gray-500">Status</h4>
            <Badge variant={caseDetails.status === 'open' ? 'default' : 'secondary'}>
              {caseDetails.status}
            </Badge>
          </div>
          
          <div>
            <h4 className="text-sm font-medium text-gray-500">Practice Area</h4>
            <p>{caseDetails.practice_area}</p>
          </div>
          
          <Separator />
          
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-2">Quick Links</h4>
            <div className="space-y-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full justify-start"
                onClick={() => window.open(`/case/${caseDetails.case_id}`, '_blank')}
              >
                <FileText className="h-4 w-4 mr-2" />
                View Case
              </Button>
              <Button variant="outline" size="sm" className="w-full justify-start">
                <Calendar className="h-4 w-4 mr-2" />
                Court Dates
              </Button>
              <Button variant="outline" size="sm" className="w-full justify-start">
                <DollarSign className="h-4 w-4 mr-2" />
                Billing
              </Button>
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
};

// New Chat Dialog Component
const NewChatDialog = ({ open, onClose, teamMembers, cases, onCreateDM, onCreateGroup, onCreateCaseChat }) => {
  const [tab, setTab] = useState('dm');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [selectedCase, setSelectedCase] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const filteredMembers = teamMembers.filter(m => 
    m.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const filteredCases = cases.filter(c =>
    c.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.case_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const handleCreate = () => {
    if (tab === 'dm' && selectedUsers.length === 1) {
      onCreateDM(selectedUsers[0]);
    } else if (tab === 'group' && selectedUsers.length > 0 && groupName) {
      onCreateGroup(groupName, selectedUsers);
    } else if (tab === 'case' && selectedCase) {
      onCreateCaseChat(selectedCase);
    }
    onClose();
    setSelectedUsers([]);
    setGroupName('');
    setSelectedCase(null);
    setSearchTerm('');
  };
  
  const toggleUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New Conversation</DialogTitle>
          <DialogDescription>
            Start a direct message, group chat, or case discussion.
          </DialogDescription>
        </DialogHeader>
        
        {/* Tab Selector */}
        <div className="flex gap-2 mb-4">
          <Button 
            variant={tab === 'dm' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => { setTab('dm'); setSelectedUsers([]); }}
          >
            <User className="h-4 w-4 mr-1" />
            Direct
          </Button>
          <Button 
            variant={tab === 'group' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setTab('group')}
          >
            <Users className="h-4 w-4 mr-1" />
            Group
          </Button>
          <Button 
            variant={tab === 'case' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setTab('case')}
          >
            <Briefcase className="h-4 w-4 mr-1" />
            Case
          </Button>
        </div>
        
        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input 
            placeholder={tab === 'case' ? 'Search cases...' : 'Search team members...'} 
            className="pl-9"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        {/* Group Name Input */}
        {tab === 'group' && (
          <div className="mb-4">
            <Label>Group Name</Label>
            <Input 
              placeholder="e.g., Smith Family Litigation Team"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
            />
          </div>
        )}
        
        {/* Selection List */}
        <ScrollArea className="h-64 border rounded-lg p-2">
          {(tab === 'dm' || tab === 'group') && (
            <div className="space-y-1">
              {filteredMembers.map(member => (
                <button
                  key={member.user_id}
                  onClick={() => tab === 'dm' ? setSelectedUsers([member.user_id]) : toggleUser(member.user_id)}
                  className={cn(
                    'w-full flex items-center gap-3 p-2 rounded-lg transition-colors',
                    selectedUsers.includes(member.user_id)
                      ? 'bg-blue-50 border border-blue-200'
                      : 'hover:bg-gray-50'
                  )}
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={member.picture} />
                    <AvatarFallback>{getInitials(member.name)}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium">{member.name}</p>
                    <p className="text-xs text-gray-500">{member.role}</p>
                  </div>
                  {tab === 'group' && (
                    <Checkbox checked={selectedUsers.includes(member.user_id)} />
                  )}
                </button>
              ))}
            </div>
          )}
          
          {tab === 'case' && (
            <div className="space-y-1">
              {filteredCases.map(c => (
                <button
                  key={c.case_id}
                  onClick={() => setSelectedCase(c.case_id)}
                  className={cn(
                    'w-full flex items-center gap-3 p-2 rounded-lg transition-colors',
                    selectedCase === c.case_id
                      ? 'bg-amber-50 border border-amber-200'
                      : 'hover:bg-gray-50'
                  )}
                >
                  <div className="h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center">
                    <Briefcase className="h-4 w-4 text-amber-600" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium">{c.title}</p>
                    <p className="text-xs text-gray-500">{c.case_number || c.case_id}</p>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {c.status}
                  </Badge>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleCreate}
            disabled={
              (tab === 'dm' && selectedUsers.length !== 1) ||
              (tab === 'group' && (selectedUsers.length === 0 || !groupName)) ||
              (tab === 'case' && !selectedCase)
            }
          >
            Start Chat
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Global Search Dialog Component
const GlobalSearchDialog = ({ open, onClose, onSelectMessage }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    setSearched(true);
    try {
      const res = await chatAPI.searchMessages(searchQuery);
      setResults(res.data.messages || []);
    } catch (error) {
      console.error('Search failed:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Search Messages</DialogTitle>
          <DialogDescription>
            Search across all your conversations
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex gap-2 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input 
              placeholder="Search messages..." 
              className="pl-9"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
            />
          </div>
          <Button onClick={handleSearch} disabled={loading || !searchQuery.trim()}>
            {loading ? 'Searching...' : 'Search'}
          </Button>
        </div>
        
        <ScrollArea className="h-80">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
            </div>
          ) : results.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <Search className="h-12 w-12 mb-3 opacity-50" />
              {searched ? (
                <p>No messages found</p>
              ) : (
                <p>Enter a search term to find messages</p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {results.map((msg) => (
                <button
                  key={msg.message_id}
                  onClick={() => {
                    onSelectMessage(msg.chat_id, msg.message_id);
                    onClose();
                  }}
                  className="w-full text-left p-3 rounded-lg hover:bg-gray-50 border transition-colors"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Avatar className="h-6 w-6">
                      <AvatarFallback className="text-xs">
                        {msg.sender?.name?.[0] || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-sm font-medium">{msg.sender?.name || 'Unknown'}</span>
                    <span className="text-xs text-gray-400">
                      {formatMessageTime(msg.created_at)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-2">{msg.content}</p>
                  <div className="text-xs text-gray-400 mt-1">
                    in {msg.chat_name || 'Chat'}
                  </div>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

// Main TeamChat Component
export default function TeamChat() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [teamMembers, setTeamMembers] = useState([]);
  const [cases, setCases] = useState([]);
  const [showNewChatDialog, setShowNewChatDialog] = useState(false);
  const [showCasePanel, setShowCasePanel] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileView, setMobileView] = useState('list'); // 'list' or 'chat'
  const [typingUsers, setTypingUsers] = useState({}); // {userId: userName}
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  
  // File attachment state
  const [attachments, setAttachments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef(null);
  
  // Refs
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  
  // WebSocket handlers for real-time updates
  const handleNewMessage = useCallback((message) => {
    setMessages(prev => {
      // Avoid duplicates
      if (prev.some(m => m.message_id === message.message_id)) return prev;
      return [...prev, message];
    });
    
    // Update chat list preview
    setChats(prev => prev.map(c => 
      c.chat_id === message.chat_id 
        ? { 
            ...c, 
            last_message_at: message.created_at, 
            last_message_preview: message.content?.slice(0, 100) 
          }
        : c
    ).sort((a, b) => new Date(b.last_message_at) - new Date(a.last_message_at)));
    
    // Show toast notification if not in active chat
    if (activeChat?.chat_id !== message.chat_id) {
      toast.info(`New message from ${message.sender?.name || 'Unknown'}`);
      
      // Increment unread count
      setUnreadCounts(prev => ({
        ...prev,
        [message.chat_id]: (prev[message.chat_id] || 0) + 1
      }));
    }
  }, [activeChat?.chat_id]);
  
  const handleReaction = useCallback((messageId, reaction) => {
    setMessages(prev => prev.map(msg => {
      if (msg.message_id !== messageId) return msg;
      
      const reactions = [...(msg.reactions || [])];
      const existingIdx = reactions.findIndex(r => r.emoji === reaction.emoji);
      
      if (reaction.action === 'added') {
        if (existingIdx >= 0) {
          reactions[existingIdx] = {
            ...reactions[existingIdx],
            count: reactions[existingIdx].count + 1,
            user_ids: [...(reactions[existingIdx].user_ids || []), reaction.user_id]
          };
        } else {
          reactions.push({ emoji: reaction.emoji, count: 1, user_ids: [reaction.user_id] });
        }
      } else if (reaction.action === 'removed' && existingIdx >= 0) {
        if (reactions[existingIdx].count <= 1) {
          reactions.splice(existingIdx, 1);
        } else {
          reactions[existingIdx] = {
            ...reactions[existingIdx],
            count: reactions[existingIdx].count - 1,
            user_ids: (reactions[existingIdx].user_ids || []).filter(id => id !== reaction.user_id)
          };
        }
      }
      
      return { ...msg, reactions };
    }));
  }, []);
  
  const handleTyping = useCallback((userId, userName, isTyping) => {
    setTypingUsers(prev => {
      if (isTyping) {
        return { ...prev, [userId]: userName };
      } else {
        const { [userId]: _, ...rest } = prev;
        return rest;
      }
    });
  }, []);
  
  // Connect to WebSocket for active chat
  const { sendTyping } = useChatSocket(
    activeChat?.chat_id,
    handleNewMessage,
    handleReaction,
    handleTyping
  );
  
  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [chatsRes, unreadRes, teamRes, casesRes] = await Promise.all([
          chatAPI.getChats(),
          chatAPI.getUnreadCounts(),
          teamAPI.getAll(),
          casesAPI.getAll(),
        ]);
        
        setChats(chatsRes.data.chats || []);
        setUnreadCounts(unreadRes.data.unread_by_chat || {});
        setTeamMembers(teamRes.data.filter(m => m.user_id !== user?.user_id));
        setCases(casesRes.data || []);
        
        // Check for chat ID in URL params
        const chatIdParam = searchParams.get('chat');
        if (chatIdParam) {
          const chatToOpen = chatsRes.data.chats?.find(c => c.chat_id === chatIdParam);
          if (chatToOpen) {
            setActiveChat(chatToOpen);
            setMobileView('chat');
          }
        }
      } catch (error) {
        console.error('Failed to load chat data:', error);
        toast.error('Failed to load chats');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [user?.user_id, searchParams]);
  
  // Load messages when active chat changes
  useEffect(() => {
    if (!activeChat) return;
    
    const loadMessages = async () => {
      setMessagesLoading(true);
      try {
        const res = await chatAPI.getMessages(activeChat.chat_id);
        setMessages(res.data.messages || []);
        
        // Mark as read
        await chatAPI.markAsRead(activeChat.chat_id);
        setUnreadCounts(prev => ({ ...prev, [activeChat.chat_id]: 0 }));
        
        // Update URL
        setSearchParams({ chat: activeChat.chat_id });
      } catch (error) {
        console.error('Failed to load messages:', error);
        toast.error('Failed to load messages');
      } finally {
        setMessagesLoading(false);
      }
    };
    
    loadMessages();
  }, [activeChat?.chat_id, setSearchParams]);
  
  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  // Handle typing indicator
  const handleInputChange = (e) => {
    setNewMessage(e.target.value);
    
    // Send typing indicator
    if (sendTyping) {
      sendTyping(true);
      
      // Clear previous timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Stop typing indicator after 2 seconds of no input
      typingTimeoutRef.current = setTimeout(() => {
        sendTyping(false);
      }, 2000);
    }
  };
  
  // Send message
  const handleSendMessage = async () => {
    if ((!newMessage.trim() && attachments.length === 0) || !activeChat || sending) return;
    
    // Stop typing indicator
    if (sendTyping) sendTyping(false);
    
    setSending(true);
    try {
      const res = await chatAPI.sendMessage(activeChat.chat_id, newMessage.trim(), attachments);
      setMessages(prev => [...prev, res.data.message]);
      setNewMessage('');
      setAttachments([]);
      
      // Update chat list preview
      setChats(prev => prev.map(c => 
        c.chat_id === activeChat.chat_id 
          ? { ...c, last_message_at: res.data.message.created_at, last_message_preview: newMessage.trim().slice(0, 100) || '📎 Attachment' }
          : c
      ).sort((a, b) => new Date(b.last_message_at) - new Date(a.last_message_at)));
      
      inputRef.current?.focus();
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };
  
  // Handle file selection
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0 || !activeChat) return;
    
    for (const file of files) {
      // Max 25MB
      if (file.size > 25 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Maximum size is 25MB`);
        continue;
      }
      
      setUploadingFile(true);
      try {
        const reader = new FileReader();
        reader.onload = async () => {
          try {
            const base64Data = reader.result.split(',')[1];
            
            const res = await chatAPI.uploadFile(activeChat.chat_id, {
              filename: file.name,
              file_data: base64Data,
              mime_type: file.type || 'application/octet-stream',
              save_to_case: activeChat.type === 'case'
            });
            
            // Add to attachments
            setAttachments(prev => [...prev, {
              file_id: res.data.file_id,
              filename: res.data.filename,
              url: res.data.url,
              type: res.data.mime_type,
              size: res.data.size
            }]);
            
            toast.success(`${file.name} uploaded`);
          } catch (error) {
            console.error('Upload error:', error);
            toast.error(`Failed to upload ${file.name}`);
          } finally {
            setUploadingFile(false);
          }
        };
        reader.onerror = () => {
          toast.error(`Failed to read ${file.name}`);
          setUploadingFile(false);
        };
        reader.readAsDataURL(file);
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
        setUploadingFile(false);
      }
    }
    
    // Clear file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  
  // Remove attachment
  const removeAttachment = (fileId) => {
    setAttachments(prev => prev.filter(a => a.file_id !== fileId));
  };
  
  // Handle reaction toggle
  const handleReactionToggle = async (messageId, emoji) => {
    if (!activeChat) return;
    
    try {
      const res = await chatAPI.addReaction(activeChat.chat_id, messageId, emoji);
      
      // Update local message state with new reaction
      setMessages(prev => prev.map(msg => {
        if (msg.message_id !== messageId) return msg;
        
        const reactions = msg.reactions || [];
        const existingIdx = reactions.findIndex(r => r.emoji === emoji);
        
        if (res.data.action === 'added') {
          if (existingIdx >= 0) {
            // Increment count
            reactions[existingIdx] = {
              ...reactions[existingIdx],
              count: reactions[existingIdx].count + 1,
              user_ids: [...(reactions[existingIdx].user_ids || []), user?.user_id]
            };
          } else {
            // Add new reaction
            reactions.push({ emoji, count: 1, user_ids: [user?.user_id] });
          }
        } else if (res.data.action === 'removed') {
          if (existingIdx >= 0) {
            if (reactions[existingIdx].count <= 1) {
              // Remove reaction entirely
              reactions.splice(existingIdx, 1);
            } else {
              // Decrement count
              reactions[existingIdx] = {
                ...reactions[existingIdx],
                count: reactions[existingIdx].count - 1,
                user_ids: (reactions[existingIdx].user_ids || []).filter(id => id !== user?.user_id)
              };
            }
          }
        }
        
        return { ...msg, reactions };
      }));
    } catch (error) {
      console.error('Failed to toggle reaction:', error);
      toast.error('Failed to add reaction');
    }
  };
  
  // Handle selecting a message from global search
  const handleSelectSearchResult = async (chatId, messageId) => {
    // Find and select the chat
    const chat = chats.find(c => c.chat_id === chatId);
    if (chat) {
      setActiveChat(chat);
      setMobileView('chat');
      // TODO: Scroll to the specific message
    } else {
      // Chat might not be in the list, try to fetch it
      try {
        const res = await chatAPI.getChat(chatId);
        if (res.data.chat) {
          setActiveChat(res.data.chat);
          setMobileView('chat');
        }
      } catch (error) {
        toast.error('Could not open chat');
      }
    }
  };
  
  // Create new DM
  const handleCreateDM = async (userId) => {
    try {
      const res = await chatAPI.createDM(userId);
      const newChat = res.data.chat;
      
      // Add to list if new
      if (!chats.find(c => c.chat_id === newChat.chat_id)) {
        setChats(prev => [newChat, ...prev]);
      }
      
      setActiveChat(newChat);
      setMobileView('chat');
      toast.success('Chat created');
    } catch (error) {
      console.error('Failed to create DM:', error);
      toast.error('Failed to create chat');
    }
  };
  
  // Create new group
  const handleCreateGroup = async (name, userIds) => {
    try {
      const res = await chatAPI.createGroup(name, userIds);
      setChats(prev => [res.data.chat, ...prev]);
      setActiveChat(res.data.chat);
      setMobileView('chat');
      toast.success('Group created');
    } catch (error) {
      console.error('Failed to create group:', error);
      toast.error('Failed to create group');
    }
  };
  
  // Create or get case chat
  const handleCreateCaseChat = async (caseId) => {
    try {
      const res = await chatAPI.createCaseChat(caseId);
      const chat = res.data.chat;
      
      if (!res.data.created) {
        // Chat already exists, just select it
        setActiveChat(chat);
      } else {
        setChats(prev => [chat, ...prev]);
        setActiveChat(chat);
      }
      
      setMobileView('chat');
      toast.success(res.data.created ? 'Case chat created' : 'Case chat opened');
    } catch (error) {
      console.error('Failed to create case chat:', error);
      toast.error('Failed to create case chat');
    }
  };
  
  // Filter chats by search
  const filteredChats = chats.filter(chat => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      chat.name?.toLowerCase().includes(query) ||
      chat.members?.some(m => m.name?.toLowerCase().includes(query))
    );
  });
  
  // Calculate total unread
  const totalUnread = Object.values(unreadCounts).reduce((sum, count) => sum + count, 0);
  
  if (loading) {
    return (
      <div className="h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }
  
  return (
    <div className="h-[calc(100vh-4rem)] flex bg-white dark:bg-gray-900" data-testid="team-chat-page">
      {/* Chat List Sidebar */}
      <div className={cn(
        'w-80 border-r flex flex-col bg-gray-50 dark:bg-gray-900',
        'md:flex',
        mobileView === 'list' ? 'flex' : 'hidden md:flex'
      )}>
        {/* Header */}
        <div className="p-4 border-b bg-white dark:bg-gray-900">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-blue-500" />
              Team Chat
              {totalUnread > 0 && (
                <Badge variant="destructive" className="ml-2">{totalUnread}</Badge>
              )}
            </h1>
            <div className="flex gap-1">
              <Button 
                size="icon" 
                variant="ghost"
                onClick={() => setShowGlobalSearch(true)}
                title="Search all messages"
                data-testid="global-search-btn"
              >
                <Search className="h-4 w-4" />
              </Button>
              <Button 
                size="icon" 
                onClick={() => setShowNewChatDialog(true)}
                data-testid="new-chat-btn"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input 
              placeholder="Filter conversations..." 
              className="pl-9"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="chat-search"
            />
          </div>
        </div>
        
        {/* Chat List */}
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            {filteredChats.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p className="font-medium">No conversations yet</p>
                <p className="text-sm mt-1">Start a new chat to begin</p>
                <Button 
                  size="sm" 
                  className="mt-4"
                  onClick={() => setShowNewChatDialog(true)}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  New Chat
                </Button>
              </div>
            ) : (
              filteredChats.map(chat => (
                <ChatListItem
                  key={chat.chat_id}
                  chat={chat}
                  isActive={activeChat?.chat_id === chat.chat_id}
                  unreadCount={unreadCounts[chat.chat_id] || 0}
                  onClick={() => {
                    setActiveChat(chat);
                    setMobileView('chat');
                  }}
                />
              ))
            )}
          </div>
        </ScrollArea>
      </div>
      
      {/* Chat Area */}
      <div className={cn(
        'flex-1 flex flex-col',
        mobileView === 'chat' ? 'flex' : 'hidden md:flex'
      )}>
        {activeChat ? (
          <>
            {/* Chat Header */}
            <div className="h-16 border-b flex items-center justify-between px-4 bg-white dark:bg-gray-900">
              <div className="flex items-center gap-3">
                <Button 
                  variant="ghost" 
                  size="icon"
                  className="md:hidden"
                  onClick={() => setMobileView('list')}
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                
                {activeChat.type === 'dm' ? (
                  <Avatar className="h-9 w-9">
                    <AvatarImage src={activeChat.members?.[0]?.picture} />
                    <AvatarFallback>{getInitials(activeChat.members?.[0]?.name)}</AvatarFallback>
                  </Avatar>
                ) : (
                  <div className={cn(
                    'h-9 w-9 rounded-full flex items-center justify-center',
                    activeChat.type === 'case' ? 'bg-amber-100 text-amber-600' : 'bg-blue-100 text-blue-600'
                  )}>
                    {activeChat.type === 'case' ? <Briefcase className="h-4 w-4" /> : <Users className="h-4 w-4" />}
                  </div>
                )}
                
                <div>
                  <h2 className="font-semibold">
                    {activeChat.type === 'dm' 
                      ? activeChat.members?.[0]?.name 
                      : activeChat.name}
                  </h2>
                  <p className="text-xs text-gray-500">
                    {activeChat.type === 'case' 
                      ? 'Case Discussion' 
                      : activeChat.type === 'group'
                        ? `${activeChat.members?.length || 0} members`
                        : activeChat.members?.[0]?.role || 'Team Member'}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                {activeChat.type === 'case' && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowCasePanel(!showCasePanel)}
                  >
                    <Briefcase className="h-4 w-4 mr-1" />
                    Case Info
                  </Button>
                )}
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {activeChat.type !== 'dm' && (
                      <DropdownMenuItem>
                        <UserPlus className="h-4 w-4 mr-2" />
                        Add Members
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem>
                      <Search className="h-4 w-4 mr-2" />
                      Search in Chat
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="text-red-600">
                      Leave Chat
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
            
            {/* Messages Area */}
            <ScrollArea className="flex-1 p-4">
              {messagesLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-500">
                  <MessageSquare className="h-12 w-12 mb-3 opacity-50" />
                  <p>No messages yet</p>
                  <p className="text-sm">Start the conversation!</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {messages.map((msg, idx) => {
                    const isOwn = msg.user_id === user?.user_id;
                    const prevMsg = messages[idx - 1];
                    const showSender = !prevMsg || prevMsg.user_id !== msg.user_id;
                    
                    return (
                      <MessageBubble
                        key={msg.message_id}
                        message={msg}
                        isOwn={isOwn}
                        showSender={showSender}
                        chatId={activeChat?.chat_id}
                        onReactionToggle={handleReactionToggle}
                      />
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>
            
            {/* Message Input */}
            <div className="p-4 border-t bg-white dark:bg-gray-900">
              {/* Typing indicator */}
              {Object.keys(typingUsers).length > 0 && (
                <div className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                  <span className="flex gap-0.5">
                    <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1 h-1 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </span>
                  <span>
                    {Object.values(typingUsers).join(', ')} {Object.keys(typingUsers).length === 1 ? 'is' : 'are'} typing...
                  </span>
                </div>
              )}
              
              {/* Attachments preview */}
              {attachments.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3 p-2 bg-gray-50 rounded-lg">
                  {attachments.map(att => (
                    <div 
                      key={att.file_id}
                      className="flex items-center gap-2 bg-white border rounded-lg px-3 py-1.5 text-sm"
                    >
                      <FileText className="h-4 w-4 text-blue-500" />
                      <span className="truncate max-w-[150px]">{att.filename}</span>
                      <button 
                        onClick={() => removeAttachment(att.file_id)}
                        className="text-gray-400 hover:text-red-500"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex items-end gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  multiple
                  onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif,.mp4,.mov"
                />
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="flex-shrink-0"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingFile}
                  data-testid="attach-file-btn"
                >
                  {uploadingFile ? (
                    <div className="h-4 w-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Paperclip className="h-4 w-4" />
                  )}
                </Button>
                <Textarea
                  ref={inputRef}
                  placeholder="Type a message..."
                  value={newMessage}
                  onChange={handleInputChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  className="min-h-[40px] max-h-32 resize-none"
                  rows={1}
                  data-testid="message-input"
                />
                <Button 
                  onClick={handleSendMessage}
                  disabled={(!newMessage.trim() && attachments.length === 0) || sending}
                  data-testid="send-message-btn"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-500 p-8">
            <div className="bg-gray-100 dark:bg-gray-800 rounded-full p-6 mb-4">
              <MessageSquare className="h-12 w-12" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Welcome to Team Chat</h2>
            <p className="text-center max-w-md mb-6">
              Connect with your team, discuss cases, and keep all your conversations organized in one place.
            </p>
            <Button onClick={() => setShowNewChatDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Start a Conversation
            </Button>
          </div>
        )}
      </div>
      
      {/* Case Info Panel */}
      {showCasePanel && activeChat?.type === 'case' && (
        <CaseInfoPanel 
          caseDetails={activeChat.case_details}
          onClose={() => setShowCasePanel(false)}
        />
      )}
      
      {/* New Chat Dialog */}
      <NewChatDialog
        open={showNewChatDialog}
        onClose={() => setShowNewChatDialog(false)}
        teamMembers={teamMembers}
        cases={cases}
        onCreateDM={handleCreateDM}
        onCreateGroup={handleCreateGroup}
        onCreateCaseChat={handleCreateCaseChat}
      />
      
      {/* Global Search Dialog */}
      <GlobalSearchDialog
        open={showGlobalSearch}
        onClose={() => setShowGlobalSearch(false)}
        onSelectMessage={handleSelectSearchResult}
      />
    </div>
  );
}
